import { useCallback } from 'react'
import { z } from 'zod'
import type { Channel } from 'phoenix'
import type { Marble } from 'src/types/domain/Marble'
import { promisifyPush } from 'src/utils/promisifyPush'
import { useGetJoinedGameChannelId } from 'src/store/socket'

type Params = Readonly<{
  gameChannel: Channel | undefined
}>

const multipliersSchema = z.object({
  game_id: z.string().uuid(),
  multipliers: z.record(z.string().uuid(), z.number())
})

export const useProbabilityMultipliers = ({ gameChannel }: Params) => {
  const joinedGameChannelGameId = useGetJoinedGameChannelId()

  const getProbabilityMultipliers = useCallback(
    async (
      marbles: Marble[],
      gameId: string
    ): Promise<{ multipliers: Record<string, number> } | undefined> => {
      if (!gameChannel || (gameChannel.state !== 'joined' && joinedGameChannelGameId !== gameId)) {
        console.error('Game channel not available')
        return
      }

      try {
        const payload = {
          items: marbles.map((marble) => marble.uuid)
        }

        const result = await promisifyPush({
          channel: gameChannel,
          eventName: 'client:multipliers:get',
          payload
        })

        const parsed = multipliersSchema.safeParse(result)

        if (!parsed.success) {
          console.error('Validation failed:', parsed.error)
          return
        }

        const { multipliers } = parsed.data

        return {
          multipliers
        }
      } catch (err) {
        console.error('Getting multipliers failed:', err)
      }
    },
    [gameChannel, joinedGameChannelGameId]
  )

  return getProbabilityMultipliers
}
